import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import HiddenVAKReport from './HiddenVAKReport';
import learningStyles from "../assets/learningStyles.jpeg";
import Acumen_25_Years_LOGO from "../assets/Acumen_25_Years_LOGO.png";

const VakSurvey = () => {
  const location = useLocation();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [submissionTime, setSubmissionTime] = useState(null);
  const generatingRef = useRef(false);
  

  // ── Trigger HiddenVAKReport PDF generation ─────────────────────────────
  const handleDownloadPDF = () => {
    if (generatingRef.current) return;   // synchronous guard — fires before any re-render
    generatingRef.current = true;
    setIsGenerating(true);
    const normalized = reportData?.data || reportData;
    setPdfData({
      ...normalized,
      submission_time: submissionTime,
    });
  };

  // Stable callback — avoids creating a new function reference on every render,
  // which could otherwise cause HiddenVAKReport to re-run its effect.
  const handlePdfComplete = useCallback(() => {
    setPdfData(null);
    setIsGenerating(false);
    generatingRef.current = false;
  }, []);

  useEffect(() => {
    if (location.state?.scores) {
      const data = location.state.scores;
      setReportData(data);
      const normalized = data?.data || data;
      setSubmissionTime(normalized?.submission_time ?? data?.submission_time ?? location.state?.submission_time ?? new Date().toISOString());
      setLoading(false);
    } else {
      const fetchFallback = async () => {
        try {
          const email = location.state?.email;
          const phone = location.state?.phone;
          const response = await fetch("response/calculate-score-vak/", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, phone }),
          });
          if (!response.ok) throw new Error("Could not retrieve report data.");
          const result = await response.json();
          setReportData(result.data);
          setSubmissionTime(result.data?.submission_time ?? new Date().toISOString());
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      fetchFallback();
    }
  }, [location.state]);

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'Satoshi, sans-serif' }}>Loading Report...</div>;
  if (error) return <div style={{ padding: '2rem', color: 'red', fontFamily: 'Satoshi, sans-serif' }}>Error: {error}</div>;
  if (!reportData) return <div style={{ padding: '2rem', fontFamily: 'Satoshi, sans-serif' }}>No data found. Please retake the survey.</div>;

  // Normalize: handle both shapes —
  //   (a) { userInfo, scores, dominantStyle }   ← API /calculate-score-vak response.data
  //   (b) { success, data: { userInfo, ... } }  ← full API response accidentally stored in state
  const normalized = reportData?.data || reportData;
  const userInfo = normalized?.userInfo || {};
  const scores = normalized?.scores || { Visual: 0, Auditory: 0, Kinesthetic: 0 };
  const dominantStyle = normalized?.dominantStyle || 'Unknown';
  const total = (scores.Visual || 0) + (scores.Auditory || 0) + (scores.Kinesthetic || 0) || 15;

  const vPct = ((scores.Visual / total) * 100).toFixed(1) + '%';
  const aPct = ((scores.Auditory / total) * 100).toFixed(1) + '%';
  const kPct = ((scores.Kinesthetic / total) * 100).toFixed(1) + '%';

  const dateStr = submissionTime
    ? new Date(submissionTime).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  const styleImplicationText = dominantStyle?.includes('/')
    ? `Because your scores are balanced across ${dominantStyle}, you can adapt fluidly between styles. Lean into whichever mode your audience prefers — use visuals for Visual peers, verbal discussion for Auditory peers, and hands-on activities for Kinesthetic peers.`
    : dominantStyle === 'Visual'
      ? `Because your primary mode is Visual, you naturally lean toward processing thoughts quickly via images and written notes. To build deeper rapport with your Auditory or Kinesthetic peers, you must deliberately adapt your language and delivery. When working with an Auditory peer, rely more on verbal phone calls and talking through the logic; when working with a Kinesthetic peer, provide hands-on prototypes or interactive whiteboard sessions rather than just handing them a visual memo.`
      : dominantStyle === 'Auditory'
        ? `Because your primary mode is Auditory, you naturally process information best through dialogue and verbal explanation. When working with Visual peers, supplement your verbal communication with written summaries or diagrams. When working with Kinesthetic peers, pair discussions with hands-on activities to ensure full engagement.`
        : `Because your primary mode is Kinesthetic, you learn best through doing and direct experience. When working with Visual peers, back up your hands-on approach with clear visuals or diagrams. When working with Auditory peers, narrate your process aloud and invite verbal feedback alongside practical exercises.`;

  const resultText = dominantStyle?.includes('/')
    ? `Based on your responses, your primary communication preference spans multiple styles (${dominantStyle}). You can adapt fluidly across visual, auditory, and kinesthetic channels depending on the situation.`
    : dominantStyle === 'Visual'
      ? `Based on your responses, your primary communication preference is Visual. You process information best when you can see it mapped out, illustrated, or demonstrated.`
      : dominantStyle === 'Auditory'
        ? `Based on your responses, your primary communication preference is Auditory. You process information best when ideas are spoken aloud, discussed, or verbally explained.`
        : `Based on your responses, your primary communication preference is Kinesthetic. You process information best through hands-on experience, movement, and direct engagement.`;

  const practicalLearningText =
    dominantStyle === 'Visual'
      ? 'For this participant, the strongest learning approach should combine visual structure with active practice. That means charts, written prompts, and diagrams should be paired with hands-on activities and short discussion-based reinforcement, so the dominant style and secondary style both get used in a balanced way.'
      : dominantStyle === 'Auditory'
        ? 'For this participant, the strongest learning approach should combine verbal discussion with written reinforcement. Spoken walkthroughs, group discussions, and verbal feedback should be paired with concise written summaries so both the dominant and secondary styles are engaged.'
        : dominantStyle?.includes('/')
          ? 'For this participant, the learning approach should draw on all three modes in equal measure. Blend visual aids, verbal discussion, and hands-on activities so each session leverages the participant\'s natural cross-modal flexibility.'
          : 'For this participant, the strongest learning approach should combine hands-on activities with verbal coaching. Role-plays, simulations, and practical exercises should be paired with guided verbal debriefs and clear visual reference material.';

  // ── Learning Style Definitions ─────────────────────────────────────────
  const learningStyleDefs = [
    {
      key: 'visual',
      color: '#3b5ec0',
      bg: '#f0f4ff',
      emoji: '👁️',
      label: 'Visual (V) Learning Preference',
      conceptual: 'Visual learners demonstrate a strong preference to rely on mental imagery, structure, and visual cues to organise and retain information, making spatial relationships and visual patterning central to their understanding of complex ideas.',
      cognitiveTitle: 'Cognitive Characteristics',
      cognitive: [
        'They prefer information presented in structured formats e.g., flowcharts, matrices, mind maps, dashboards, and annotated slides. Materials such as diagrams, charts, videos, demonstrations, and written instructions are particularly effective because they provide a structured way to interpret information.',
        'Think and recall in terms of "pictures" or visual metaphors; often imagine concepts as spatial arrangements or graphic representations.',
        'Respond well to color-coding, highlighting, and visual segmentation, which helps signal importance, sequence or causality.',
      ],
      behaviorTitle: 'Behavioral Indicators',
      behavior: [
        'When presented with a document or set of information, they often underline, annotate, or spontaneously sketch diagrams to internalise cause-effect chains or logical structure.',
        'They generally prefer to observe first, either by reading instructions or watching a demonstration, before attempting tasks.',
        'In group discussions around strategy, process, or roles, they may request visual models to clarify relationships, responsibilities, or change pathways.',
        'During presentations or training sessions, they pay close attention to slides and layout, sometimes reorganizing information before engaging with details.',
        'In conversation, they may use expressions like "I see what you mean," "show me," or "that looks right," reflecting how they mentally organize ideas.',
      ],
    },
    {
      key: 'auditory',
      color: '#b93030',
      bg: '#fff5f5',
      emoji: '👂',
      label: 'Auditory (A) Learning Preference',
      conceptual: 'Auditory learners prefer to process information through spoken language, tone, rhythm, and dialogue. They engage most effectively through listening, discussion, and verbal exchange, with attention to how something is said as well as what is said. They are highly attuned not only to words but also to how those words are delivered.',
      cognitiveTitle: 'Cognitive Characteristics',
      cognitive: [
        'Learn best when ideas are explained aloud, discussed in groups, or narrated step-by-step. Clarity often comes through hearing ideas explained and discussed rather than reading them independently.',
        'Often process information by talking it through or mentally replaying what they have heard; spoken instruction tends to be more impactful than written guidance.',
        'Use internal "self-talk" or discussion to organise and clarify ideas, sometimes weighing options verbally.',
        'Are sensitive to tone, pace, and emphasis, which shape how they interpret meaning and intent.',
      ],
      behaviorTitle: 'Behavioural Indicators',
      behavior: [
        'In meetings, may ask for verbal explanations or want key points talked through before making decisions.',
        'Often refer back to past conversations or specific phrases to maintain clarity and alignment.',
        'Prefer dialogue-based feedback or coaching over purely written notes, and may summarise understanding aloud.',
        'Common phrases such as "I hear what you\'re saying," "tell me," or "that sounds right" reflect this preference.',
        'Engage comfortably in discussions, often thinking aloud, and may disengage if information is presented only visually without explanation.',
      ],
    },
    {
      key: 'kinesthetic',
      color: '#d9790a',
      bg: '#fffbeb',
      emoji: '🧠',
      label: 'Kinesthetic (K) Learning Preference',
      conceptual: 'Kinesthetic learners prefer learning through physical engagement, movement, and direct experience. They understand information best by doing, interacting, and applying ideas in real or simulated contexts.',
      cognitiveTitle: 'Cognitive Characteristics',
      cognitive: [
        'Prefer learning by engaging in activities such as role-plays, simulations, hands-on exercises, or trial-and-error experimentation.',
        'In structured settings, they benefit from activities that involve participation—simulations, role play, or applied exercises—rather than extended periods of observation or listening.',
        'Use movement, gesture, and physical interaction (e.g., walking while thinking, using models, or interacting with tools) to support focus and problem-solving.',
        'Often internalize concepts more deeply when they have personally experienced or enacted a scenario, rather than when they have only read or heard about it.',
        'Internalize concepts more deeply through direct experience, often learning by trying, adjusting, and refining in real time.',
      ],
      behaviorTitle: 'Behavioral Indicators',
      behavior: [
        'When facing change, process redesign, or new initiatives, they may advocate for pilots, "test runs," or micro-experiments to validate approaches before full-scale rollout.',
        'They gravitate toward interactive formats such as business simulations, scenario-based workshops, or live case studies, where they can act out decisions and experience consequences.',
        'Use gestures, whiteboards, or physical objects to organise and refine ideas during discussions.',
        'Use phrases such as "let me try," "get a handle on it," or "how does that feel?" reflecting an action-oriented approach.',
      ],
    },
  ];

  // ── Dominant style keys (handles multi e.g. "Visual/Auditory") ──────────
  const dominantKeys = new Set(
    (dominantStyle || '').split('/').map(s => s.trim().toLowerCase())
  );

  // ── Reorder style definitions so the dominant style(s) come first ───────
  // Page 3 always shows orderedStyleDefs[0] (the dominant style).
  // Page 4 always shows the remaining two (in their original relative order).
  const orderedStyleDefs = [...learningStyleDefs].sort((a, b) => {
    const aDom = dominantKeys.has(a.key) ? 0 : 1;
    const bDom = dominantKeys.has(b.key) ? 0 : 1;
    return aDom - bDom;
  });
  const shortLabel = (key) => key.charAt(0).toUpperCase() + key.slice(1);
  const page3Style = orderedStyleDefs[0];
  const page4Styles = orderedStyleDefs.slice(1, 3);
  const page4Label = page4Styles.map(s => shortLabel(s.key)).join(' & ');

  const renderStyleCard = (style) => {
    const isHighlighted = dominantKeys.has(style.key);
    return (
      <div
        key={style.key}
        className="item"
        style={{
          paddingLeft: '1rem',
          marginBottom: '1.5rem',
          borderTop: isHighlighted ? `1.5px solid ${style.color}` : '1px solid var(--line)',
          borderRight: isHighlighted ? `1.5px solid ${style.color}` : '1px solid var(--line)',
          borderBottom: isHighlighted ? `1.5px solid ${style.color}` : '1px solid var(--line)',
          borderLeft: isHighlighted ? `5px solid ${style.color}` : `4px solid ${style.color}`,
          background: isHighlighted ? style.bg : '#fff',
          boxShadow: isHighlighted ? `0 0 0 3px ${style.bg}` : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
          <strong style={{ display: 'block', color: style.color, fontSize: '1.15rem' }}>
            {style.emoji} {style.label}
          </strong>
          {isHighlighted && (
            <div style={{
              flexShrink: 0,
              background: style.color, color: '#fff',
              fontSize: '10px', fontWeight: 800,
              padding: '3px 10px', borderRadius: '999px',
              textTransform: 'uppercase', letterSpacing: '.06em',
              whiteSpace: 'nowrap',
            }}>
              ★ Your Style
            </div>
          )}
        </div>
        <p style={{ margin: '0 0 0.75rem', color: '#334155', fontSize: '0.92rem', textAlign: 'justify' }}>
          <strong>Conceptual Definition:</strong> {style.conceptual}
        </p>
        <p style={{ margin: '0 0 0.35rem', fontWeight: 700, fontSize: '0.9rem', color: '#0f2744' }}>{style.cognitiveTitle}</p>
        <ul style={{ margin: '0 0 0.75rem 0', paddingLeft: '1.25rem', fontSize: '0.88rem', color: '#475569' }}>
          {style.cognitive.map((pt, i) => <li key={i} style={{ marginBottom: '0.3rem' }}>{pt}</li>)}
        </ul>
        <p style={{ margin: '0 0 0.35rem', fontWeight: 700, fontSize: '0.9rem', color: '#0f2744' }}>{style.behaviorTitle}</p>
        <ul style={{ margin: '0 0 0.75rem 0', paddingLeft: '1.25rem', fontSize: '0.88rem', color: '#475569' }}>
          {style.behavior.map((pt, i) => <li key={i} style={{ marginBottom: '0.3rem' }}>{pt}</li>)}
        </ul>

        {style.key === 'visual' && (
          <div style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: '4px', marginTop: '1rem' }}>
            <strong style={{ fontSize: '0.9rem', color: '#3b5ec0' }}>👁️ Visual Mode (Eye Accessing Cues):</strong>
            <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem', fontSize: '0.9rem' }}>
              <li><strong>Up right:</strong> Imagining / future visuals</li>
              <li><strong>Up left:</strong> Recalling past visuals</li>
              <li><strong>Straight ahead (unfocused):</strong> Processing images</li>
            </ul>
          </div>
        )}

        {style.key === 'auditory' && (
          <div style={{ background: '#fef2f2', padding: '0.75rem', borderRadius: '4px', marginTop: '1rem' }}>
            <strong style={{ fontSize: '0.9rem', color: '#b93030' }}>👂 Auditory Mode (Eye Accessing Cues):</strong>
            <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem', fontSize: '0.9rem' }}>
              <li><strong>Side right:</strong> Imagining future sounds</li>
              <li><strong>Side left:</strong> Recalling past sounds</li>
              <li><strong>Down left:</strong> Internal dialogue (self-talk)</li>
            </ul>
          </div>
        )}

        {style.key === 'kinesthetic' && (
          <div style={{ background: '#fffbeb', padding: '0.75rem', borderRadius: '4px', marginTop: '1rem' }}>
            <strong style={{ fontSize: '0.9rem', color: '#d9790a' }}>🧠 Kinesthetic Mode (Eye Accessing Cues):</strong>
            <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem', fontSize: '0.9rem' }}>
              <li><strong>Down right:</strong> Accessing feelings/emotions</li>
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="vak-report" id="vak-report-content">
        <link href="https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700,900&display=swap" rel="stylesheet" />
        <style>{`
        :root {
          --text-xs: clamp(0.75rem, 0.7rem + 0.25vw, 0.875rem);
          --text-sm: clamp(0.875rem, 0.8rem + 0.35vw, 1rem);
          --text-base: clamp(1rem, 0.95rem + 0.25vw, 1.125rem);
          --text-lg: clamp(1.125rem, 1rem + 0.75vw, 1.4rem);
          --text-xl: clamp(1.45rem, 1.2rem + 1.1vw, 2rem);
          --space-1: 0.25rem; --space-2: 0.5rem; --space-3: 0.75rem; --space-4: 1rem;
          --space-5: 1.25rem; --space-6: 1.5rem; --space-8: 2rem; --space-10: 2.5rem;
          --space-12: 3rem; --space-16: 4rem; --space-20: 5rem;
          --navy: #0f2744; --blue: #c62828; --gold: #d6a23f;
          --ink: #1f2937; --muted: #6b7280;
          --bg: #f4f6f8; --card: #ffffff; --line: #d9e0e7; --soft: #eef2f6;
          --radius: 20px; --radius-sm: 12px;
          --shadow: 0 18px 40px rgba(14,39,68,.10);
          --font-body: 'Satoshi', sans-serif;

          /* Logo panel: fluid width clamped between 180px and 500px */
            --logo-w: clamp(180px, 42vw, 500px);
            --logo-h: clamp(90px,  21vw, 250px);
            --logo-img-h: clamp(30px, 6vw, 65px);
            --logo-pl: clamp(20px, 5vw, 60px);
        }
        .vak-report {
          font-family: var(--font-body);
          background: linear-gradient(180deg, #eef4f9 0%, #f7fafc 100%);
          color: var(--ink);
          line-height: 1.5;
        }
        .vak-report .report {
          max-width: 1100px;
          margin: 0 auto;
          padding: var(--space-8) var(--space-4) var(--space-16);
        }
        .vak-report .page {
          background: var(--card);
          border: 1px solid var(--line);
          border-radius: 28px;
          box-shadow: var(--shadow);
          overflow: hidden;
          margin-bottom: var(--space-8);
        }
        /* Cover */
        .vak-report .cover {
          min-height: 92vh;
          position: relative;
          background: radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%),
                      linear-gradient(135deg, #A81026, #C4163A 55%, #7B0F2B);
          color: white;
        }
        .vak-report .cover-inner {
          padding: var(--space-16);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          min-height: 92vh;
        }
        .vak-report .brand {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--space-4);
          position: relative;
          z-index: 2;
          padding-right: clamp(0px, 38vw, 200px);
        }
        .vak-report .logo-panel {
            position: absolute;
            top: 0;
            right: 0;
            width: var(--logo-w);
            height: var(--logo-h);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
            pointer-events: none;
          }
          .vak-report .logo-panel-inner {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            position: relative;
            z-index: 2;
            padding-left: var(--logo-pl);
            padding-bottom: clamp(8px, 2vw, 25px);
          }
          .vak-report .logo-img {
            height: var(--logo-img-h);
            width: auto;
            object-fit: contain;
            flex-shrink: 0;
          }
            .vak-report .badge {
          border: 1px solid rgba(255,255,255,.18);
          background: rgba(255,255,255,.08);
          padding: clamp(.3rem, .8vw, .6rem) clamp(.5rem, 1.2vw, 1rem);
          border-radius: 999px;
          font-size: clamp(0.58rem, 1.5vw, 0.85rem);
          white-space: nowrap;
          flex-shrink: 0;
        }
        .vak-report .cover-title { margin-top: var(--space-16); }
        .vak-report .cover-title h2 {
          margin: 0 0 var(--space-4);
          font-size: clamp(2.6rem, 5vw, 4.8rem);
          line-height: .96;
          max-width: 9ch;
          
          border-left: 6px solid #fff;
  padding-left: 1rem;
        }
        .vak-report .cover-title p {
          font-size: var(--text-lg);
          max-width: 100%;
          color: rgba(255,255,255,.84);
          text-align: justify;
        }
        .vak-report .cover-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: var(--space-4);
          margin-top: var(--space-12);
        }
        .vak-report .meta {
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.16);
          border-radius: 18px;
          padding: 1rem 1.1rem;
          font-size: 1.2rem;
        }
        .vak-report .meta span {
          display: block;
          font-size: var(--text-xs);
          color: rgba(255,255,255,.66);
          margin-bottom: .25rem;
        }
        .vak-report .meta strong { font-size: var(--text-base); color: white; }
        .vak-report .cover-badges {
          display: flex;
          flex-wrap: wrap;
          gap: .6rem;
          margin-top: var(--space-8);
        }
        .vak-report .cover-badge {
          background: rgba(255,255,255,.12);
          border: 1px solid rgba(255,255,255,.18);
          padding: .5rem .9rem;
          border-radius: 999px;
          font-size: 0.95rem;
        }
        .vak-report .footer-note {
          display: flex;
          justify-content: space-between;
          gap: var(--space-4);
          color: rgba(255,255,255,.72);
          font-size: var(--text-xs);
          margin-top: var(--space-10);
        }
        /* Page inner */
        .vak-report .page-inner { padding: var(--space-10); }
        .vak-report .topbar {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: var(--space-4);
          margin-bottom: var(--space-8);
        }
        .vak-report .section-kicker {
          color: var(--blue);
          font-size: var(--text-xs);
          text-transform: uppercase;
          letter-spacing: .12em;
          font-weight: 700;
        }
        .vak-report .section-title {
          margin: .35rem 0 0;
          font-size: var(--text-xl);
          color: var(--navy);
        }
        .vak-report .summary-grid { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }
        .vak-report .card {
          background: linear-gradient(180deg, #fff 0%, #fbfcfd 100%);
          border: 1px solid var(--line);
          border-radius: var(--radius);
          padding: var(--space-6);
        }
        .vak-report .card h3 { margin: 0 0 .75rem; font-size: var(--text-lg); color: var(--navy); }
        .vak-report .lead { color: #334155; font-size: var(--text-base); margin: 0; text-align: justify; }
        .vak-report .pill {
          display: inline-flex;
          align-items: center;
          gap: .45rem;
          padding: .5rem .85rem;
          border-radius: 999px;
          font-size: var(--text-xs);
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .06em;
          white-space: nowrap;
        }
        .vak-report .pill.blue { background: #fef2f2; color: var(--blue); }
        /* Bars */
        .vak-report .bars { display: grid; gap: 1.5rem; margin-top: 1rem; }
        .vak-report .bar-row {
          display: grid;
          grid-template-columns: 120px 1fr 60px;
          gap: 1rem;
          align-items: center;
        }
        .vak-report .bar-label { font-size: 1rem; font-weight: 700; color: var(--navy); }
        .vak-report .track {
          height: 24px;
          background: #e9eef3;
          border-radius: 999px;
          overflow: hidden;
          position: relative;
        }
        .vak-report .fill { height: 100%; border-radius: 999px; position: relative; }
        .vak-report .fill.vis { background: linear-gradient(90deg, #1e3a8a, #3b82f6); }
        .vak-report .fill.aud { background: linear-gradient(90deg, #7f1d1d, #ef4444); }
        .vak-report .fill.kin { background: linear-gradient(90deg, #b45309, #f59e0b); }
        .vak-report .score-box { text-align: right; font-variant-numeric: tabular-nums; }
        .vak-report .score-box strong { display: block; color: var(--navy); font-size: 1.1rem; }
        .vak-report .small { font-size: var(--text-xs); color: var(--muted); }
        /* List items */
        .vak-report .list { display: grid; gap: 0rem; }
        .vak-report .item {
          border: 1px solid var(--line);
          border-radius: 16px;
          padding: 1.25rem;
          background: #fff;
        }
        .vak-report .item p { margin: 0.5rem 0 0; color: #475569; font-size: 0.9rem; text-align: justify; }
        /* Callout */
        .vak-report .callout {
          background: linear-gradient(135deg, #fff7f7, #fbeaea);
          border: 1px solid var(--line);
          border-radius: 18px;
          padding: 1.25rem 1.5rem;
          color: #334155;
          text-align: justify;
        }
        /* Back cover */
        .vak-report .back-cover { color: white; }
        .vak-report .back-cover .page-inner {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        .vak-report .back-cover-header {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: center;
          gap: 0.5rem;
          padding: clamp(0.9rem, 2.5vw, 1.5rem) clamp(0.9rem, 3vw, 2.5rem);
          border-bottom: 1px solid rgba(255,255,255,0.15);
        }
        .vak-report .closing h2 { margin: 0 0 1rem; font-size: clamp(1.4rem, 4vw, 3.25rem); color: #FFFFFF; }
        .vak-report .closing p { max-width: 100%; color: rgba(255,255,255,.84); font-size: clamp(0.85rem, 2vw, 1.2rem); text-align: justify; }
        .vak-report .contact-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(140px, 100%), 1fr));
          gap: clamp(0.4rem, 1.2vw, 1rem);
          margin-top: var(--space-8);
        }
        .vak-report .contact {
          background: rgba(168,16,38,.1);
          border: 1px solid rgba(255,255,255,.16);
          border-radius: clamp(10px, 2vw, 16px);
          padding: clamp(0.6rem, 1.5vw, .9rem);
          min-width: 0;
        }
        .vak-report .contact span {
          display: block;
          color: rgba(255,255,255,.64);
          font-size: var(--text-xs);
          margin-bottom: .3rem;
        }
        .vak-report .contact strong { font-size: clamp(0.78rem, 1.6vw, 0.92rem); word-break: break-word; }
        .vak-report .light { color: rgba(255,255,255,.68); }
        @media print {
          .vak-report { background: white; }
          .vak-report .report { max-width: none; padding: 0; }
          .vak-report .page { box-shadow: none; border-radius: 0; margin: 0 0 12px; border: 1px solid #ddd; }
          #vak-pdf-btn { display: none !important; }
        }
        /* ── Responsive: Tablet (≤ 768px) ── */
        @media (max-width: 768px) {
          /* Outer wrapper */
          .vak-report .report { padding: 1.25rem 0.75rem 3rem; }
          .vak-report .page { border-radius: 20px; margin-bottom: 1.25rem; }
          /* Cover – logo panel scales to ~64% */
          


          .vak-report .cover { min-height: 88vh; }
          .vak-report .cover-inner { padding: 2.25rem; min-height: 88vh; }
          .vak-report .cover-title { margin-top: 2.5rem; }
          .vak-report .cover-title h2 { font-size: clamp(2rem, 5vw, 3.2rem); }
          .vak-report .cover-grid { gap: 0.75rem; margin-top: 1.5rem; }
          /* Inner pages */
          .vak-report .page-inner { padding: 1.75rem; }
          .vak-report .topbar { flex-wrap: wrap; gap: 0.5rem; }
          .vak-report .pill { font-size: 10px; padding: .35rem .6rem; }
          .vak-report .bar-row { grid-template-columns: 90px 1fr 44px; gap: .5rem; }
          .vak-report .contact-grid { grid-template-columns: repeat(auto-fit, minmax(min(140px, 100%), 1fr)); }
        }
        /* ── Responsive: Mobile (≤ 480px) ── */
        @media (max-width: 480px) {
          /* Outer wrapper */
          .vak-report .report { padding: 0.75rem 0.5rem 2.5rem; }
          .vak-report .page { border-radius: 16px; margin-bottom: 0.9rem; }
          /* Cover – logo panel scales to ~42% so it fits a ~360px page width */
          .vak-report .cover { min-height: 100svh; }
          .vak-report .cover-inner { padding: 1.25rem; min-height: 100svh; }
          


          .vak-report .cover-title { margin-top: 1.5rem; }
          .vak-report .cover-title h2 { font-size: clamp(1.6rem, 7vw, 2.2rem); line-height: 1.08; }
          .vak-report .cover-title p { font-size: 0.875rem; }
          .vak-report .cover-grid { grid-template-columns: repeat(auto-fit, minmax(min(130px,100%), 1fr)); gap: 0.5rem; margin-top: 1.1rem; }
          .vak-report .meta { padding: 0.6rem 0.75rem; }
          .vak-report+-
          
          
          
          .cover-badges { margin-top: 0.9rem; gap: 0.4rem; }
          .vak-report .cover-badge { font-size: 0.7rem; padding: 0.3rem 0.55rem; }
          .vak-report .footer-note { flex-direction: column; gap: 0.2rem; font-size: 0.68rem; margin-top: 1rem; }
          /* Inner pages */
          .vak-report .page-inner { padding: 1rem; }
          .vak-report .topbar { flex-wrap: wrap; gap: 0.4rem; margin-bottom: 1rem; }
          .vak-report .section-title { font-size: 1.25rem; }
          .vak-report .pill { font-size: 9px; padding: .25rem .5rem; }
          /* Cards */
          .vak-report .summary-grid { gap: 0.75rem; }
          .vak-report .card { padding: 0.875rem; border-radius: 14px; }
          .vak-report .card h3 { font-size: 0.95rem; margin-bottom: 0.45rem; }
          .vak-report .lead { font-size: 0.84rem; }
          /* Bar chart */
          .vak-report .bar-row { grid-template-columns: 68px 1fr 30px; gap: 0.4rem; }
          .vak-report .bar-label { font-size: 0.77rem; }
          .vak-report .track { height: 18px; }
          .vak-report .score-box strong { font-size: 0.8rem; }
          /* List items */
          .vak-report .item { padding: 0.8rem; border-radius: 12px; }
          .vak-report .item p { font-size: 0.8rem; }
          /* Callout */
          .vak-repor*t .callout { padding: 0.875rem 1rem; border-radius: 14px; }
          /* Back cover */
          .vak-report .closing h2 { font-size: clamp(1.45rem, 5.5vw, 2rem); }
          .vak-report .closing p { font-size: 0.88rem; max-width: 100%; }
          .vak-report .contact-grid { grid-template-columns: 1fr; gap: 0.5rem; margin-top: 1rem; }        }
        /* ── Responsive: Tiny (≤ 390px) — covers 360×640 and similar ── */
        @media (max-width: 390px) {
          .vak-report .report { padding: 0.5rem 0.35rem 2rem; }
          .vak-report .page { border-radius: 12px; margin-bottom: 0.75rem; }
          /* Logo panel: shrink to 160px wide so it doesn't eat into left content */
          

          
          /* Cover inner: tighter padding */
          .vak-report .cover { min-height: 100svh; }
          .vak-report .cover-inner { padding: 1rem; min-height: 100svh; }
          /* Brand row: limit right padding so badge stays visible */
          .vak-report .brand { padding-right: clamp(0px, 44vw, 160px); }
          .vak-report .badge { font-size: 0.55rem; padding: 0.25rem 0.5rem; }
          /* Title */
          .vak-report .cover-title { margin-top: 1rem; }
          .vak-report .cover-title h2 { font-size: clamp(1.35rem, 6.5vw, 1.7rem); line-height: 1.1; max-width: 100%; }
          .vak-report .cover-title p { font-size: 0.78rem; max-width: 100%; }
          /* Meta grid: single column so "Assessment Date" never overflows */
          .vak-report .cover-grid { grid-template-columns: 1fr; gap: 0.4rem; margin-top: 0.9rem; }
          .vak-report .meta { padding: 0.5rem 0.65rem; font-size: 1rem; border-radius: 12px; }
          .vak-report .meta strong { font-size: 0.82rem; }
          /* Badges */
          .vak-report .cover-badges { margin-top: 0.75rem; gap: 0.3rem; }
          .vak-report .cover-badge { font-size: 0.65rem; padding: 0.25rem 0.45rem; }
          /* Footer */
          .vak-report .footer-note { flex-direction: column; gap: 0.15rem; font-size: 0.62rem; margin-top: 0.75rem; }
          /* Inner pages */
          .vak-report .page-inner { padding: 0.85rem; }
          .vak-report .bar-row { grid-template-columns: 58px 1fr 26px; gap: 0.3rem; }
          .vak-report .bar-label { font-size: 0.72rem; }
          .vak-report .track { height: 16px; }
          .vak-report .score-box strong { font-size: 0.72rem; }
          .vak-report .contact-grid { grid-template-columns: 1fr; gap: 0.4rem; margin-top: 0.75rem; }
        }
        /* ── Download Button ── */
        #vak-pdf-btn {
          position: fixed;
          bottom: 2rem;
          right: 2rem;
          z-index: 9999;
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.75rem 1.4rem;
          background: linear-gradient(135deg, #F3AC26, #D79A1E);
          color: white;

          border: 1px solid #F3AC26;

          border-radius: 999px;

         box-shadow:
          0 8px 24px rgba(198,40,40,0.35),
          0 0 14px rgba(243,173,38,0.45);

          cursor: pointer;
          transition: transform 0.18s ease,
                      box-shadow 0.18s ease,
                      opacity 0.18s ease;
        }
        #vak-pdf-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(198,40,40,0.45);
        }
        #vak-pdf-btn:active:not(:disabled) { transform: translateY(0); }
        #vak-pdf-btn:disabled { opacity: 0.72; cursor: not-allowed; }
        #vak-pdf-btn .btn-spinner {
          width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.35);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
          flex-shrink: 0;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

        {/* ── Floating PDF Download Button ── */}
        <button
          id="vak-pdf-btn"
          onClick={handleDownloadPDF}
          disabled={isGenerating}
          aria-label="Download PDF Report"
        >
          {isGenerating ? (
            <>
              <span className="btn-spinner" />
              Generating PDF…
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download PDF
            </>
          )}
        </button>

        <div className="report">

          {/* ══════════════════════════════════════════════════════
            PAGE 1 — COVER
        ══════════════════════════════════════════════════════ */}
          <section className="page cover">
            <div className="cover-inner">

              {/* Logo panel — matching VAK SVG shape */}
             <div className="logo-panel">
                <svg
                  width="100%" height="100%"
                  viewBox="0 0 500 250"
                  style={{ position: 'absolute', top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none"
                >
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img src={Acumen_25_Years_LOGO} alt="Acumen Logo" className="logo-img" />
                </div>
              </div>

              <div className="brand">
                <div className="badge">Confidential Individual Report</div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1 }}>
                <div className="cover-title">
                  <h2>VAK Learning Style Assessment</h2>
                  <p>
                    This comprehensive assessment report presents an analysis of your communication and learning preferences using the VAK framework, helping identify how you most effectively understand information, engage with others, and develop skills.
                  </p>
                </div>

                <div className="cover-grid">
                  <div className="meta"><span>Participant</span><strong>{userInfo.name || '—'}</strong></div>
                  <div className="meta"><span>Assessment Date</span><strong>{dateStr}</strong></div>
                  <div className="meta"><span>Primary Style</span><strong>{dominantStyle}</strong></div>
                </div>

                <div className="cover-badges">
                  {['Assessment Summary', 'VAK Distribution', 'Style Implications'].map((b) => (
                    <span key={b} className="cover-badge">{b}</span>
                  ))}
                </div>
              </div>

              <div className="footer-note">
                <div>Based on the Visual / Auditory / Kinesthetic (VAK) Preference Survey framework.</div>
                {/* <div>Max 15 points distributed across all three modes.</div> */}
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
            PAGE 2 — ASSESSMENT SUMMARY
        ══════════════════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 2</div>
                  <h2 className="section-title">Assessment Summary</h2>
                </div>
                <div className="pill blue">Dominant Mode: {dominantStyle}</div>
              </div>

              <div className="summary-grid">
                <div className="card">
                  <h3>Understanding Your Mental Map</h3>
                  <p className="lead">While each individual has one primary mode in which they prefer to communicate, we all have the ability to move from one mode to another depending on the situation—just as a right-handed person can still use their left hand, even if the right is more comfortable.</p>
                  <p className="lead" style={{ marginTop: '1rem' }}>The survey you just completed was designed to identify your predominant perceptual preference. These scores reveal your dominant mental map, providing valuable insights into how you best receive information and how you can build deeper rapport with others by matching their communication styles.</p>
                </div>

                <div className="card">
                  <h3>Why Identifying Your Style Matters</h3>
                  <p className="lead">Research highlights that when individuals are aware of their own learning styles, they can apply appropriate techniques to make absorbing new information significantly faster and more efficient. Knowing your preferred style is not only helpful in classroom or training settings, but it also plays a crucial role in everyday life by increasing problem-solving, analyzing, and overall analytical skills.</p>
                  <p className="lead" style={{ marginTop: '1rem' }}>As educational researcher J.M. Reid defined it, a learning style is simply <em>"an individual's natural, habitual, and preferred ways of absorbing, processing, and retaining new information and skills."</em> For leaders and educators, understanding these styles means you can design your communication more effectively, engaging your audience and facilitating their success.</p>
                </div>

                <div className="card" style={{ background: 'white' }}>
                  <h3>The Three Representational Systems</h3>
                  <p className="lead" style={{ marginBottom: '1.5rem' }}>Below is an elaborated breakdown of the VAK learning styles, outlining the unique characteristics, preferences, and language associated with each style.</p>

                  {/* Image */}
                  <div style={{ textAlign: 'center' }}>
                    <img
                      src={learningStyles}
                      alt="VAK Learning Styles Chart"
                      onError={e => { e.currentTarget.style.display = 'none'; }}
                      style={{ maxWidth: '100%', height: 'auto', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', maxHeight: '300px', objectFit: 'contain' }}
                    />
                  </div>
                </div>

                <div className="card">
                  <h3>Your VAK Distribution</h3>
                  {dominantStyle === 'Invalid/Incomplete' && (
                    <div style={{ background: '#fff7ed', border: '1px solid #fcd34d', borderRadius: '12px', padding: '0.85rem 1rem', marginBottom: '1rem', color: '#92400e', fontSize: 'var(--text-sm)' }}>
                      ⚠️ One or more answers contained unexpected values and could not be scored. Please retake the survey to see your distribution.
                    </div>
                  )}
                  <div className="bars">
                    <div className="bar-row">
                      <div className="bar-label">Visual (V)</div>
                      <div className="track"><div className="fill vis" style={{ width: vPct }}></div></div>
                      <div className="score-box"><strong>{scores.Visual}</strong></div>
                    </div>
                    <div className="bar-row">
                      <div className="bar-label">Auditory (A)</div>
                      <div className="track"><div className="fill aud" style={{ width: aPct }}></div></div>
                      <div className="score-box"><strong>{scores.Auditory}</strong></div>
                    </div>
                    <div className="bar-row">
                      <div className="bar-label">Kinesthetic (K)</div>
                      <div className="track"><div className="fill kin" style={{ width: kPct }}></div></div>
                      <div className="score-box"><strong>{scores.Kinesthetic}</strong></div>
                    </div>
                  </div>
                  <p className="small" style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                    Scores represent the number of choices matching each style (Total: {total} questions).
                  </p>
                </div>

                <div className="callout">
                  <strong style={{ color: '#c62828' }}>Your Result:</strong>
                  <p style={{ marginTop: '0.5rem' }}>{resultText}</p>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
            PAGE 3 — LEARNING STYLE DEFINITIONS (VISUAL)
        ══════════════════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 3</div>
                  <h2 className="section-title">Learning Style Definitions</h2>
                </div>
                <div className="pill blue" style={{ background: page3Style.bg, color: page3Style.color }}>{shortLabel(page3Style.key)}</div>
              </div>

              {/* Intro paragraph */}
              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <h3>VAK Parameters: Conceptual Definitions Across Roles and Levels</h3>
                <p className="lead">
                  The VAK (Visual–Auditory–Kinesthetic) framework describes how individuals tend to perceive, process, and internalize information most effectively. Rather than assuming a single method suits everyone, it emphasizes that people often favor different sensory channels. These three representational systems should be seen as <strong>preferences rather than fixed categories</strong>. Most people use all three to some extent, though one often feels more natural or dominant.
                </p>
              </div>

              <div className="list">
                {[page3Style].map(renderStyleCard)}
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
            PAGE 3b — LEARNING STYLE DEFINITIONS (AUDITORY & KINESTHETIC)
        ══════════════════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 4</div>
                  <h2 className="section-title">Learning Style Definitions</h2>
                </div>
                <div className="pill blue" style={{ background: page4Styles[0]?.bg, color: page4Styles[0]?.color }}>{page4Label}</div>
              </div>

              <div className="list">
                {page4Styles.map(renderStyleCard)}
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
            BACK COVER
        ══════════════════════════════════════════════════════ */}
          <section className="page back-cover">
            <div
              style={{
                background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,#A81026,#C4163A 55%,#7B0F2B)`,
                minHeight: '56vh',
              }}
            >
              {/* Header bar */}
              <div className="back-cover-header">
                <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '.12em', fontWeight: 700, color: 'rgba(255,255,255,0.66)' }}>
                  {/* Back Cover */}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', letterSpacing: '.05em' }}>
                  Acumen Business Catalyst
                </div>
              </div>

              <div className="page-inner">
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1 }}>
                  <div className="closing" style={{ padding: '0.1rem' }}>
                    <h2>Adaptability is Key</h2>
                    <p>While you naturally lean toward a {dominantStyle} communication style, the most effective leaders learn how to adapt their message to suit the preferred modes of their audience. Use these insights to build stronger connections with your team.</p>
                  </div>
                </div>

                <div>
                  <div className="card" style={{ marginTop: '1.5rem', background: 'rgba(255,255,255,.06)', borderColor: 'rgba(255,255,255,.14)', color: 'white' }}>
                    <h3 style={{ color: 'white' }}>Practical Learning Design</h3>
                    <p className="lead" style={{ color: 'rgba(255,255,255,.84)' }}>{practicalLearningText}</p>
                  </div>

                  <div className="contact-grid">
                    <div className="contact"><span>Brand</span><strong>Acumen Business Catalyst</strong></div>
                    <div className="contact"><span>Focus</span><strong>Leadership • Culture • Capability</strong></div>
                    <div className="contact"><span>Report type</span><strong>VAK Assessment Report</strong></div>
                  </div>

                  <p className="light" style={{ marginTop: '1.5rem', fontSize: '0.75rem' }}>
                    This report is generated from the VAK Learning Style Survey framework.
                  </p>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>

      {/* ── Hidden PDF generator (mounts only while generating) ── */}
      {pdfData && (
        <HiddenVAKReport
          participantData={pdfData}
          onComplete={handlePdfComplete}
        />
      )}
    </>
  );
};

export default VakSurvey;